import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { trigger, transition, animate, style } from "@angular/animations";
import { SharedService } from "../../services/shared.service";
import { KeyCodes, focusableSelector } from "../../models/key-codes.model";
import * as Draggabilly from "draggabilly/draggabilly";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "modal-popup",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  animations: [
    trigger("opacity", [
      transition(":enter", [
        style({ opacity: "0" }),
        animate("100ms ease-out", style({ opacity: "1" })),
      ]),
      transition(":leave", [
        style({ opacity: "1" }),
        animate("200ms ease-out", style({ opacity: "0" })),
      ]),
    ]),
  ],
  template: `
    <ng-container *ngIf="!isOpen; else modalPopupTemplate"></ng-container>

    <ng-template #modalPopupTemplate>
      <div
        [@opacity]
        class="popup-overlay"
        [ngClass]="{
          'is-flexible': flexible,
          'is-popupDisplay': popupDisplay,
          'is-fullWidth': fullWidth
        }"
        role="alert"
      >
        <div class="l-spread popup-overlayClose"></div>

        <section
          class="l-layer-up modal-popup-body draggable"
          [class.wide]="wide"
        >
          <header
            *ngIf="!hideHeader"
            class="l-align-y popup-top popup-top--modal handle"
          >
            <div class="l-cell-spread-x">
              <h2 *ngIf="title" class="popup-title">
                {{ title | translate }}
              </h2>
            </div>
            <button
              #closeBtn
              [attr.aria-label]="'close'"
              (click)="onCancel()"
              class="popup-close"
            ></button>
          </header>

          <div class="l-cell-spread-y h-full l-pos-relative popup-main-hold">
            <main #modalContent class="popup-main" [class.pad]="padding">
              <ng-content></ng-content>
            </main>
          </div>
        </section>
      </div>
    </ng-template>
  `,
})
export class ModalPopupComponent implements OnInit {
  constructor(private sharedService: SharedService) {}

  @Input() title: string;
  @Input() show: string;
  @Input() flexible: boolean = false;
  @Input() popupDisplay: boolean = false;
  @Input() isOpen: boolean = false;
  @Input() fullWidth: boolean = false;
  @Input() hideHeader: boolean = false;
  @Input() focusOnOpen: boolean = true;
  @Input() padding: boolean = true;
  @Input() wide: boolean = false;
  @Input() draggable: boolean = false;
  @Output() onOpen = new EventEmitter<any>();
  @Output() onClose = new EventEmitter<any>();

  showPopup: boolean = false;
  keyCodes: KeyCodes = new KeyCodes();
  focusableSelector: string = focusableSelector;
  _modalContent: ElementRef;
  freeKeyboard: boolean;
  keyboardTrapCounter: number = -1;
  boundKeyboardTrap = this.keyboardTrap.bind(this);
  boundOnCancelByEscKey = this.onCancelByEscKey.bind(this);
  destroyed: boolean = false;

  @ViewChild("modalContent", { static: false }) set modalContent(
    modalContent: ElementRef
  ) {
    requestAnimationFrame(() => {
      // the component may have been destroyed before this frame ran —
      // adding the listeners then would leak them with no owner to remove
      if (this.destroyed) return;
      if (modalContent) {
        this._modalContent = modalContent;
        this.freeKeyboard = false;
        this.removeKeyupListeners();
        document.addEventListener("keyup", this.boundKeyboardTrap);
        document.addEventListener("keyup", this.boundOnCancelByEscKey);
        if (this.focusOnOpen) this.focusOnFirstInput();

        // if (this.draggable) {
        //   var elem = document.querySelector(".draggable");
        //   var draggie = new Draggabilly(elem, {
        //     handle: ".handle",
        //   });
        // }
      } else {
        this.freeKeyboard = true;
        this._modalContent = null;
        this.removeKeyupListeners();
      }
    });
  }

  @ViewChild("closeBtn", { static: false }) closeBtn: ElementRef;

  ngOnInit() {}
  ngOnDestroy() {
    this.destroyed = true;
    this.removeKeyupListeners();
  }

  removeKeyupListeners() {
    document.removeEventListener("keyup", this.boundKeyboardTrap);
    document.removeEventListener("keyup", this.boundOnCancelByEscKey);
  }
  ngOnChanges() {
    // this.sharedService.modal = this;
  }

  onCancel() {
    this.onClose.emit();
  }

  onCancelByEscKey(event: KeyboardEvent) {
    if (event.keyCode === this.keyCodes.escape) {
      let htmlCloseBtns = document.querySelectorAll(".popup-close");
      let lastBtn: Element = htmlCloseBtns[htmlCloseBtns.length - 1];
      if (lastBtn) lastBtn["click"]();
    }
  }

  keyboardTrap(event: KeyboardEvent) {
    if (
      this.freeKeyboard ||
      !this._modalContent ||
      event.keyCode !== this.keyCodes.tab
    )
      return;

    const el = this._modalContent.nativeElement;
    const focusables: NodeList = el.querySelectorAll(this.focusableSelector);
    const focusablesLength = focusables.length;
    let currentFocusableIndex = [].findIndex.call(
      focusables,
      (i) => i === document.activeElement
    );
    if (!event.shiftKey) {
      this.keyboardTrapCounter++;
    } else {
      this.keyboardTrapCounter--;
    }
    if (currentFocusableIndex !== -1)
      this.keyboardTrapCounter = currentFocusableIndex;

    // if larger than last, focus on first
    if (this.keyboardTrapCounter > focusablesLength) {
      this.keyboardTrapCounter = 0;
      focusables[this.keyboardTrapCounter] &&
        focusables[this.keyboardTrapCounter]["focus"]();
    }
    // if smaller than first, focus on last
    if (this.keyboardTrapCounter < 0) {
      this.keyboardTrapCounter = focusablesLength;
      focusables[this.keyboardTrapCounter - 1] &&
        focusables[this.keyboardTrapCounter - 1]["focus"]();
    }
  }

  focusOnFirstInput() {
    const el = this._modalContent.nativeElement;
    const focusable = el.querySelector(this.focusableSelector);
    focusable && focusable.focus();
  }
}
